import { useCallback } from 'react';

import { useNotificationContext } from '@/modules/notifications/NotificationContext/hooks/useNotificationContext';
import { NotificationKind } from '@/types/schema';

/**
 * Returns a callback that marks every unread validation-questions notification
 * for `householdId` as read.
 *
 * Acting on the questions from anywhere in the app answers the notification, so
 * the inbox (and the app bar callout that keys off it) shouldn't keep asking.
 * There can be more than one when several runs have produced questions.
 */
export function useMarkValidationQuestionNotificationsRead(
  householdId: string
): () => void {
  const { unreadValidationQuestionNotifications, markRead } =
    useNotificationContext();

  return useCallback(() => {
    unreadValidationQuestionNotifications
      .filter(
        (notification) =>
          notification.kind === NotificationKind.ValidationQuestions &&
          notification.household?.id === householdId
      )
      .forEach((notification) => {
        // markRead reports its own failures and never rejects, so there's
        // nothing to await or handle here.
        void markRead(notification.id);
      });
  }, [householdId, markRead, unreadValidationQuestionNotifications]);
}
